// @flow
import { functions, auth } from 'firebase';
import { EThree } from '@virgilsecurity/e3kit-browser';
// import { EThree } from '@virgilsecurity/e3kit';
// import { EThree } from '@virgilsecurity/e3kit-browser/dist/browser.asmjs.es';
import { captureException, captureMessage } from '@sentry/browser';
import { AuthController } from '../../controllers';

class Security {
  userPublicKey: ?Object;

  recipientPublicKey: ?Object;

  recipientsPublicKeys: Object;

  userUid: ?string;

  therapistUid: ?string;

  eThree: Object;

  constructor() {
    // Vao ser usadas para poder encrypt e decrypt od dados
    this.userPublicKey = null;
    this.recipientPublicKey = null;
    this.recipientsPublicKeys = {};
  }

  setUserPublicKey = (publicKey: Object) => {
    this.userPublicKey = publicKey;
  };

  setRecipientPublicKey = (publicKey: Object) => {
    this.recipientPublicKey = publicKey;
  };

  addRecipientPublicKey = (patientUid: string, publicKey: string) => {
    this.recipientsPublicKeys[patientUid] = publicKey;
  };

  getUserPublicKey = () => {
    return this.userPublicKey;
  };

  getRecipientPublicKey = () => {
    return this.recipientPublicKey;
  };

  setUids = (userUid: ?string, therapistUid: ?string) => {
    // We save the user and therapist Uid to be used later in case their public keys were not set
    // and they're returned null when trying to encrypt message
    // If they come as null we dont set them to preserve previously saved uids
    if (userUid) {
      this.userUid = userUid;
    }

    if (therapistUid) {
      this.therapistUid = therapistUid;
    }
    // console.log('Setou as uids no security: ', this.userUid, this.therapistUid)
  };

  getUserUid = () => {
    return this.userUid;
  };

  getTherapistUid = () => {
    return this.therapistUid;
  };

  getEThree = async () => {
    if (!this.eThree) {
      // Vai tentar setar o eThree duas vezes se for preciso
      try {
        captureMessage('eThree not initialized, trying to initialize again');
        await this.initialize(this.therapistUid);
        return this.eThree;
      } catch (error) {
        captureException(error);
        try {
          captureMessage('eThree not initialized, trying to initialize again 2');
          await this.initialize(this.therapistUid);
          return this.eThree;
        } catch (error2) {
          captureException(error2);
          throw error2;
        }
      }
    }
    return this.eThree;
  };

  // loadWasm = async () => {
  //   try {
  //     const e3kit = await import('@virgilsecurity/e3kit-browser');
  //     // this.setState({wasm});
  //     // console.log('E3 retornado: ', e3kit)
  //     const alice = await e3kit.EThree.initialize(createGetToken('alice'));
  //   } catch(err) {
  //     console.error(`Unexpected error in loadWasm. [Message: ${err.message}]`);
  //   }
  // };

  initialize = async (therapistUid): Promise<Object> => {
    const initializeFunction = () =>
      AuthController.getVirgilJwtToken(therapistUid || this.therapistUid);

    try {
      const eThree = await EThree.initialize(initializeFunction);
      this.eThree = eThree;
    } catch (error) {
      captureException(error);
      throw error;
    }
  };

  restorePrivateKeyOrRegister = async (userUid: string) => {
    const eThree = await this.getEThree();

    if (userUid) {
      const hasLocalPrivateKey = await eThree.hasLocalPrivateKey();
      console.log({ eThree });
      console.log({ hasLocalPrivateKey });
      console.log('UID da Terapeuta', userUid);

      const updateTherapistDevice = await AuthController.checkUpdatedDevice();

      if (updateTherapistDevice.updated_device) {
        try {
          console.log("Tá com device diferente e vai atualizar a chave na cloud")
          return await this.cleanAndRestore(userUid);
        } catch (err) {
          captureMessage('Não conseguiu dar CleanAndRestore', err);
        }
      } else {
        return !hasLocalPrivateKey ? await this.restorePrivateKey(userUid) : null;
      }
    } else {
      captureMessage('Tentou fazer o restorePrivateKeyOrRegister sem userUid');
    }
  };

  restorePrivateKey = async userUid => {
    const eThree = await this.getEThree();

    try {
      await eThree.restorePrivateKey(userUid);
      console.log('Deu o restore sem problemas');
    } catch (error) {
      console.log('ERROR no restorePrivateKey: ', error);
      captureException(error);
      captureMessage(`Error no restorePrivateKey(userUid) -> ${error.name} ${userUid}`);
      if (error.name === 'WrongKeyknoxPasswordError') {
        try {
          captureMessage(`Caiu no WrongKeyknox(userUid) -> ${error.name} ${userUid}`);
          // await this.rotatePrivateKey(userUid);
        } catch (err) {
          captureMessage(`Error no restorePrivateKey(userUid) -> ${error.name} ${userUid}`);
        }
      } else if (
        error.name === 'PrivateKeyAlreadyExistsError' ||
        error.name === 'IdentityAlreadyExistsError'
      ) {
        this.cleanAndRestore(userUid || this.therapistUid);
      } else if (error.name === 'MissingPrivateKeyError') {
        try {
          await this.registerUserAndBackupPrivateKey(userUid || this.therapistUid);
        } catch (error) {
          console.log('Catch do registerUserAndBackupPrivateKey depois de tentar', error);
        }
      } else if (error.name === 'PrivateKeyNoBackupError') {
        try {
          await this.backupUserPrivateKey(userUid || this.therapistUid);
          await eThree.restorePrivateKey(userUid || this.therapistUid);
        } catch (error) {
          captureMessage(`Não conseguiu dar backup/restore e vai tentar register()`, error);
        }
      } else {
        await this.registerUserAndBackupPrivateKey(userUid || this.therapistUid);
      }
    }
  };

  cleanAndRestore = async userUid => {
    const eThree = await this.getEThree();

    try {
      console.log('Antes do clean', await eThree.hasLocalPrivateKey());
      await eThree.cleanup();
      console.log('Depois  do clean', await eThree.hasLocalPrivateKey());
      await eThree.restorePrivateKey(userUid || this.therapistUid);
      console.log('Depois  do restore', await eThree.hasLocalPrivateKey());
    } catch (error) {
      console.log('Error no cleanAndRestore', error);
      console.log('Current therapist UID: ', userUid);

      captureException(error);
    }
  };

  resetPrivateKey = async uid => {
    const eThree = await this.getEThree();

    try {
      console.log('no resetPrivateKey');
      await eThree.cleanup();
      console.log('limpou as private key');
      await eThree.resetPrivateKeyBackup();
      await eThree.backupPrivateKey(uid);
      await eThree.restorePrivateKey(uid);
      console.log('has local private key', await eThree.hasLocalPrivateKey());
    } catch (error) {
      console.log('Error no rotatePrivateKey', error);
    }
  };

  rotatePrivateKey = async uid => {
    const eThree = await this.getEThree();
    try {
      console.log('no rotatePrivateKey');
      await eThree.resetPrivateKeyBackup();
      console.log('no rotatePrivateKey -> reset');
      await eThree.cleanup();
      console.log('no rotatePrivateKey -> clean');
      await eThree.rotatePrivateKey();
      console.log('no rotatePrivateKey -> rotate');
      await eThree.backupPrivateKey(uid);
      console.log('no rotatePrivateKey -> backup');
      captureMessage(`Deu rotatePrivateKey com sucesso`);
      console.log('has local private key', await eThree.hasLocalPrivateKey());
    } catch (error) {
      console.log('Error no rotatePrivateKey', error);
    }
  };

  registerUserAndBackupPrivateKey = async (password: string) => {
    try {
      await this.registerUser();
      await this.backupUserPrivateKey(password);
      console.log('Registrou e fez backup');
    } catch (error) {
      if (error.name === 'IdentityAlreadyExistsError') {
        this.cleanAndRestore(password || this.therapistUid);
      }
      console.log('Error no registerUserAndBackupPrivateKey: ', error);
      // captureException(error);
      throw error;
    }
  };

  registerUser = async () => {
    const eThree = await this.getEThree();

    try {
      // console.log('VAI TENTAR REGISTRAR');
      await eThree.register();
      // console.log('REGISTROU NO VIRGIL: ', response);
    } catch (regError) {
      // captureException(regError);
      throw regError;
      // console.log('Error no Register: ', regError, regError.name);
    }
  };

  backupUserPrivateKey = async (password: string) => {
    const eThree = await this.getEThree();

    try {
      await eThree.backupPrivateKey(password);
      console.log('Fez o backup da Private Key');
    } catch (error) {
      if (error.name === 'MissingPrivateKeyError') {
        this.registerUserAndBackupPrivateKey(password);
        captureMessage(`Register after MissingPrivateKey - ${password}`)
      }
      console.log('Error no backupPrivateKey: ', error);
      throw error;
    }
  };

  findUsers = async (identity: string) => {
    const eThree = await this.getEThree(identity);
    let key = null;
    // console.log('Identity in lookupPublicKeys: ', identity);
    try {
      key = await eThree.findUsers(identity);
      // console.log('CHAVES DO PUBLICKEY: ', identity);
    } catch (error) {
      captureException(error);
      // console.log('Error no PublicKey: ', error, error.identities);
    }
    return key;
  };

  encryptText = async (text: string, recipientPublicKey: string) => {
    const eThree = await this.getEThree();
    // console.log('recipientPublicKey no encryptText: ', recipientPublicKey)
    let encryptedText = null;

    try {
      encryptedText = await eThree.encrypt(text, recipientPublicKey);
      // console.log('TEXTO ENCRIPTADO: ', encryptedText);
    } catch (error) {
      // console.log('Error no Encript: ', error);
      captureException(error);
      throw error;
    }

    return encryptedText;
  };

  decryptText = async (text: string, senderPublicKey: Object) => {
    const eThree = await this.getEThree();
    let decryptedText = null;
    // console.log('VAI DECRIPTAR COM PublicKey: ', senderPublicKey)
    try {
      decryptedText = await eThree.decrypt(text, senderPublicKey);
      // console.log('TEXTO DECRIPTADO: ', decryptedText);
    } catch (error) {
      // console.log('Error no Decrypt: ', error);
      // // console.log('Error Text: ', text);
      captureException(error);
      throw error;
    }
    return decryptedText;
  };

  hasLocalPrivateKey = async () => {
    const eThree = await this.getEThree();
    const hasLocalPrivateKey = await eThree.hasLocalPrivateKey();
    return hasLocalPrivateKey;
  };

  restoreUserPrivateKey = async (password: string) => {
    const eThree = await this.getEThree();

    try {
      await eThree.restorePrivateKey(password);
      // console.log('Restored PrivateKey: ');
    } catch (error) {
      // console.log('Error no restoreUserPrivateKey: ', error);
      // captureException(error);
    }
  };

  cleanupUserKey = async () => {
    const eThree = await this.getEThree();

    try {
      await eThree.cleanup();
      captureMessage('Fez o cleanUp das chaves');
    } catch (error) {
      // console.log('ERRO NO CLEANUP: ', error);
      captureException(error);
    }
  };
}

export default new Security();
